import { useEffect, useState } from 'react'
import { TopBar } from './components/TopBar'
import { Header } from './components/Header'
import { PageOverride } from './components/PageOverride'
import { ReservationFlow } from './components/ReservationFlow'
import { Events } from './sections/Events'
import { Hero } from './sections/Hero'
import { SearchBar } from './sections/SearchBar'
import { UspV2 } from './sections/UspV2'
import { StyleCards } from './sections/StyleCards'
import { Activities } from './sections/Activities'
import { Areal } from './sections/Areal'
import { PartnerPortal } from './sections/Partner'
import { CheckIn } from './sections/CheckIn'
import { USP } from './sections/USP'
import { Accommodation } from './sections/Accommodation'
import { Offers } from './sections/Offers'
import { VoucherPurchase } from './sections/VoucherPurchase'
import { Freizeit } from './sections/Freizeit'
import { Menu } from './sections/Menu'
import { Gallery } from './sections/Gallery'
import { Brigady } from './sections/Brigady'
import { Contact } from './sections/Contact'
import { Footer } from './sections/Footer'
import { Rules } from './sections/Rules'
import { RulesFishing } from './sections/RulesFishing'
import { Reschedule } from './sections/Reschedule'
import { Gdpr } from './sections/Gdpr'
import { Podminky } from './sections/Podminky'
import { Cenik } from './sections/Cenik'
import { UbytovaciRad } from './sections/UbytovaciRad'
import { InfoPrijezd, Faq } from './sections/InfoPages'
import { PlatbaDodani, ReklamacniRad } from './sections/PlatbaReklamace'
import { PaymentMethodStep } from './components/PaymentMethodStep'

export default function App() {
  // Jednoduchý hash router: #partner = partner portal, jinak hlavní stránka
  const [route, setRoute] = useState<string>(() => window.location.hash || '#')
  useEffect(() => {
    // Prohlížeč si jinak při změně hashe obnovuje starou pozici scrollu
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual'
    function onHash() {
      const h = window.location.hash || '#'
      // Podstránka: nahoru HNED, ještě před překreslením (nezávisle na efektu níže)
      if (/^#(podminky|info-|gdpr|pravidla|preplanovat|partner|checkin|preview-payment)/.test(h) || h === '#' || h === '#top') {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior })
      }
      setRoute(h)
    }
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [])

  // Přepnutí „stránky" (#info-cenik, #podminky …) musí začít nahoře — dřív
  // zůstal scroll tam, kde byl na hlavní stránce (Robin 24. 10.: „stránka
  // nevyjede nahoru"). A naopak: kotva na hlavní stránce (#galerie) klikaná
  // z podstránky existuje až po vykreslení, takže na ni dojedeme ručně.
  useEffect(() => {
    const isSubpage = /^#(podminky|info-|gdpr|pravidla|preplanovat|partner|checkin|preview-payment)/.test(route)
    const toTop = () => window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior })
    const id = route.replace(/^#/, '').split('?')[0]
    if (isSubpage || !id || id === 'top') {
      // hned + po vykreslení (prohlížeč jinak nechá starou pozici)
      toTop(); requestAnimationFrame(toTop); setTimeout(toTop, 120)
      return
    }
    // Kotva na hlavní stránce: sekce nad ní (menu z API, fotky) ještě dorůstají,
    // takže dojíždíme opakovaně, dokud se rozložení neusadí. Sticky lišta se
    // odečte, ať nezakryje nadpis sekce.
    const timers: number[] = []
    const scrollToId = () => {
      const el = document.getElementById(id)
      if (!el) return
      const header = document.querySelector('header')?.getBoundingClientRect().height || 0
      const top = el.getBoundingClientRect().top + window.scrollY - header - 8
      window.scrollTo({ top: Math.max(0, top), left: 0, behavior: 'instant' as ScrollBehavior })
    }
    for (const ms of [0, 120, 300, 600, 1000, 1600]) timers.push(window.setTimeout(scrollToId, ms))
    return () => timers.forEach((t) => clearTimeout(t))
  }, [route])

  if (route.startsWith('#checkin')) {
    return <CheckIn />
  }

  // Dev preview — výběr platební metody mimo modal (jen pro screenshoty/zkoušení)
  if (route.startsWith('#preview-payment')) {
    return (
      <>
        <TopBar />
        <Header />
        <section className="py-12">
          <div className="container-x max-w-xl">
            <PaymentMethodStep priceCzk={500} variableSymbol="126000020" onContinue={() => (window.location.hash = '')} />
          </div>
        </section>
        <Footer />
      </>
    )
  }

  if (route.startsWith('#podminky')) {
    return (
      <>
        <TopBar />
        <Header />
        <PageOverride page="podminky" title="Obchodní podmínky"><Podminky /></PageOverride>
        <Footer />
      </>
    )
  }

  if (route.startsWith('#info-rad')) {
    return (
      <>
        <TopBar />
        <Header />
        <PageOverride page="rad" title="Ubytovací řád"><UbytovaciRad /></PageOverride>
        <Footer />
      </>
    )
  }

  if (route.startsWith('#info-cenik')) {
    return (
      <>
        <TopBar />
        <Header />
        <Cenik />
        <Footer />
      </>
    )
  }

  if (route.startsWith('#info-prijezd')) {
    return (
      <>
        <TopBar />
        <Header />
        <PageOverride page="prijezd" title="Příjezd a odjezd"><InfoPrijezd /></PageOverride>
        <Footer />
      </>
    )
  }

  if (route.startsWith('#info-platba')) {
    return (<><TopBar /><Header /><PageOverride page="platba" title="Platební a dodací podmínky"><PlatbaDodani /></PageOverride><Footer /></>)
  }

  if (route.startsWith('#info-reklamace')) {
    return (<><TopBar /><Header /><PageOverride page="reklamace" title="Reklamační řád"><ReklamacniRad /></PageOverride><Footer /></>)
  }

  if (route.startsWith('#info-faq')) {
    return (
      <>
        <TopBar />
        <Header />
        <PageOverride page="faq" title="Časté dotazy"><Faq /></PageOverride>
        <Footer />
      </>
    )
  }

  if (route.startsWith('#gdpr')) {
    return (
      <>
        <TopBar />
        <Header />
        <PageOverride page="gdpr" title="Ochrana osobních údajů"><Gdpr /></PageOverride>
        <Footer />
      </>
    )
  }

  if (route.startsWith('#pravidla-rybolov')) {
    return (
      <>
        <TopBar />
        <Header />
        <PageOverride page="pravidla_rybolov" title="Pravidla rybolovu"><RulesFishing /></PageOverride>
        <Footer />
      </>
    )
  }

  if (route.startsWith('#pravidla')) {
    return (
      <>
        <TopBar />
        <Header />
        <PageOverride page="pravidla" title="Pravidla rezervace"><Rules /></PageOverride>
        <Footer />
      </>
    )
  }

  if (route.startsWith('#preplanovat')) {
    return (
      <>
        <TopBar />
        <Header />
        <Reschedule />
        <Footer />
      </>
    )
  }

  if (route.startsWith('#partner')) {
    return (
      <>
        <TopBar />
        <Header />
        <PartnerPortal />
        <Footer />
      </>
    )
  }

  return (
    <>
      <TopBar />
      <Header />
      <Hero />
      <SearchBar />
      <StyleCards />
      <Events />
      <UspV2 />
      <Activities />
      <Areal />
      <Accommodation />
      <Offers />
      <VoucherPurchase />
      <Freizeit />
      <Menu />
      <Gallery />
      <Brigady />
      <Contact />
      <Footer />
      <ReservationFlow />
    </>
  )
}
